import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CommonButton } from '@/components/ui/common-button';
import { CommonSelect } from '@/components/ui/common-select';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import Pagination from '@/components/ui/pagination';
import { useToast } from '@/components/ui/use-toast';
import { usePagination } from '@/hooks/usePagination';
import reportService from '@/services/reportService';
import masterDataService from '@/services/masterDataService';
import SecondaryTripReportTable from './SecondaryTripReportTable';

const SecondaryTripReport = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 100 });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalReports, setTotalReports] = useState(0);
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      start_date: params.get('start_date') || '',
      end_date: params.get('end_date') || '',
      trip_status: params.get('trip_status') || '',
      transporter_ids: params.get('transporter_ids') || '',
      include_summary: true,
    };
  });
  const [errors, setErrors] = useState({ start_date: '', end_date: '' });
  const [transporters, setTransporters] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    if (!searchParams.get('page')) {
      const params = new URLSearchParams(searchParams);
      params.set('page', '1');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const data = await masterDataService.getTransporters(0, 500, false);
        setTransporters(Array.isArray(data) ? data : (data.data || []));
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to load filter options. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsInitialLoad(false);
      }
    };
    loadMasterData();
  }, [toast]);

  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      handleGenerateReport(true);
    }
  }, [currentPage, pageSize, filters.start_date, filters.end_date, filters.trip_status, filters.transporter_ids]);

  const updateUrlWithFilters = (newFilters) => {
    const params = new URLSearchParams(searchParams);
    ['start_date', 'end_date', 'trip_status', 'transporter_ids'].forEach((key) => {
      if (newFilters[key]) params.set(key, newFilters[key]);
      else params.delete(key);
    });
    params.set('page', '1');
    setSearchParams(params);
  };

  const transporterOptions = transporters
    .filter((t) => t && t.id)
    .map((t) => ({ value: t.id.toString(), label: t.company_name }));

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    updateUrlWithFilters(newFilters);
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleDateChange = (key, value) => {
    handleFilterChange(key, value ? value.split('T')[0] : '');
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!filters.start_date) newErrors.start_date = 'Start date is required';
    if (!filters.end_date) newErrors.end_date = 'End date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildApiFilters = (skip, limit) => {
    const apiFilters = { ...filters, skip, limit };
    if (apiFilters.transporter_ids) {
      apiFilters.transporter_ids = apiFilters.transporter_ids.split(',').map((id) => id.trim()).filter(Boolean);
    }
    if (apiFilters.trip_status) {
      apiFilters.trip_status = apiFilters.trip_status.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return apiFilters;
  };

  const handleGenerateReport = async (isAutoGenerate = false) => {
    if (isAutoGenerate && (!filters.start_date || !filters.end_date)) return;
    if (!isAutoGenerate && !validateForm()) return;

    setGenerating(true);
    try {
      const urlPage = parseInt(searchParams.get('page'), 10) || 1;
      const urlSize = parseInt(searchParams.get('size'), 10) || 100;
      const skip = isAutoGenerate ? (urlPage - 1) * urlSize : (currentPage - 1) * pageSize;
      const limit = isAutoGenerate ? urlSize : pageSize;

      const response = await reportService.getSecondaryTripReports(buildApiFilters(skip, limit));
      const formattedReports = reportService.formatSecondaryTripReportData(response.data || []);
      setReports(formattedReports);
      setTotalReports(response.total ?? formattedReports.length);
      if (response.summary) setSummary(response.summary);

      if (!isAutoGenerate) {
        toast({ title: 'Success', description: `Generated report with ${formattedReports.length} trips.` });
      }
    } catch (error) {
      if (!isAutoGenerate) {
        toast({
          title: 'Error',
          description: `Failed to generate report: ${error.message || 'Please try again.'}`,
          variant: 'destructive',
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleExportReport = async () => {
    if (!validateForm()) return;
    setExporting(true);
    try {
      const blob = await reportService.exportSecondaryTripReportExcel(buildApiFilters());
      const filename = `secondary-trip-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Success', description: 'Report exported to Excel successfully.' });
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to export report: ${error.message || 'Please try again.'}`,
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const tripStatusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'COMPLETED', label: 'Completed' },
  ];

  if (isInitialLoad) return <FullScreenLoader isLoading={isInitialLoad} />;

  return (
    <div className="space-y-6 relative">
      <FullScreenLoader isLoading={exporting} />
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Secondary Trip Report</h1>
          <p className="text-xs text-muted-foreground">
            Secondary movement trips — distributor and field-officer
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CommonButton onClick={() => handleGenerateReport(false)} disabled={generating}>
            Generate Report
          </CommonButton>
          {reports.length > 0 && (
            <CommonButton onClick={handleExportReport} disabled={exporting} variant="outline">
              Export to Excel
            </CommonButton>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative">
        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'start_date' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <DateTimeInput
              label="Start Date"
              id="start_date"
              value={filters.start_date ? `${filters.start_date}T00:00` : ''}
              onChange={(e) => handleDateChange('start_date', e.target.value)}
              error={errors.start_date}
              required
              maxDate={new Date()}
              onOpenChange={(open) => setActiveDropdown(open ? 'start_date' : (activeDropdown === 'start_date' ? null : activeDropdown))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'end_date' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <DateTimeInput
              label="End Date"
              id="end_date"
              value={filters.end_date ? `${filters.end_date}T00:00` : ''}
              onChange={(e) => handleDateChange('end_date', e.target.value)}
              error={errors.end_date}
              required
              maxDate={new Date()}
              onOpenChange={(open) => setActiveDropdown(open ? 'end_date' : (activeDropdown === 'end_date' ? null : activeDropdown))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'trip_status' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <CommonSelect
              label="Trip Status"
              id="trip_status"
              value={filters.trip_status}
              onValueChange={(value) => handleFilterChange('trip_status', value)}
              options={tripStatusOptions}
              placeholder="Select trip status"
              searchable
              onOpenChange={(open) => setActiveDropdown(open ? 'trip_status' : (activeDropdown === 'trip_status' ? null : activeDropdown))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'transporter_ids' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <CommonSelect
              label="Transporter"
              id="transporter_ids"
              value={filters.transporter_ids}
              onValueChange={(value) => handleFilterChange('transporter_ids', value)}
              options={transporterOptions}
              placeholder="Select transporter"
              searchable
              onOpenChange={(open) => setActiveDropdown(open ? 'transporter_ids' : (activeDropdown === 'transporter_ids' ? null : activeDropdown))}
            />
          </div>
        </div>
      </div>

      {summary && (
        <Card>
          <CardHeader>
            <CardTitle>Report Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border p-4 rounded">
                <h3 className="font-semibold">Total Trips</h3>
                <p className="text-2xl">{summary.total_trips || 0}</p>
              </div>
              <div className="border p-4 rounded">
                <h3 className="font-semibold">Total Distance</h3>
                <p className="text-2xl">{summary.total_distance || 0} km</p>
              </div>
              <div className="border p-4 rounded">
                <h3 className="font-semibold">Avg. Trip Duration</h3>
                <p className="text-2xl">{summary.average_trip_duration || 0} hrs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {reports.length > 0 && (
        <TableBlurLoader isLoading={generating}>
          <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform mt-4" style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
            <div className="border border-border py-4 rounded-[16px] bg-background">
              <SecondaryTripReportTable reports={reports} />
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalReports / pageSize)}
              totalItems={totalReports}
              pageSize={pageSize}
              onPageChange={setPage}
              className="border-t border-border pt-4"
            />
          </div>
        </TableBlurLoader>
      )}
    </div>
  );
};

export default SecondaryTripReport;
